import { useEffect, useId, useMemo, useState, type CSSProperties } from 'react';
import Chip from '../../components/Chip';
import EmptyState from '../../components/EmptyState';
import Icon from '../../components/Icon';
import { EntityIcon } from './ExplorerShared';
import { classLabel, relationGroups } from './explorerModel';
import { classColor, type GraphModel } from './graphModel';
import './orbit-view.css';

interface OrbitViewProps {
  model: GraphModel;
  rootId: string;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onRootChange: (id: string) => void;
}

const GROUP_PAGE_SIZE = 6;
const NODE_PAGE_SIZE = 6;

function entityStyle(klass: string): CSSProperties {
  return { '--orbit-color': classColor(klass) } as CSSProperties;
}

function satellitePosition(index: number, count: number) {
  const offset = count === 2 ? 0 : count === 4 ? -45 : -90;
  const angle = (offset + (index * 360) / count) * (Math.PI / 180);
  return { x: 50 + Math.cos(angle) * 34, y: 50 + Math.sin(angle) * 35 };
}

/** A bounded overview: satellites represent real relation groups, never all nodes. */
export function OrbitView({ model, rootId, selectedId, onSelect, onRootChange }: OrbitViewProps) {
  const groups = useMemo(() => relationGroups(model, rootId), [model, rootId]);
  const [groupKey, setGroupKey] = useState<string | null>(null);
  const [groupPage, setGroupPage] = useState(0);
  const [nodePage, setNodePage] = useState(0);
  const [query, setQuery] = useState('');
  const resultsId = useId();
  const searchId = useId();
  const root = model.byId.get(rootId);

  useEffect(() => {
    setGroupKey(null);
    setGroupPage(0);
    setNodePage(0);
    setQuery('');
  }, [rootId]);

  const groupPages = Math.max(1, Math.ceil(groups.length / GROUP_PAGE_SIZE));
  const currentGroupPage = Math.min(groupPage, groupPages - 1);
  const visibleGroups = groups.slice(currentGroupPage * GROUP_PAGE_SIZE, (currentGroupPage + 1) * GROUP_PAGE_SIZE);
  const activeGroup = visibleGroups.find((group) => group.key === groupKey) ?? visibleGroups[0];
  const filteredNodes = useMemo(() => {
    const term = query.trim().toLocaleLowerCase('de');
    return (activeGroup?.nodes ?? []).filter((node) => !term || `${node.label} ${node.detail}`.toLocaleLowerCase('de').includes(term));
  }, [activeGroup, query]);
  const nodePages = Math.max(1, Math.ceil(filteredNodes.length / NODE_PAGE_SIZE));
  const currentNodePage = Math.min(nodePage, nodePages - 1);
  const visibleNodes = filteredNodes.slice(currentNodePage * NODE_PAGE_SIZE, (currentNodePage + 1) * NODE_PAGE_SIZE);
  const neighbourCount = model.neighbours.get(rootId)?.size ?? 0;

  function chooseGroup(key: string) {
    setGroupKey(key);
    setNodePage(0);
    setQuery('');
  }

  function changeGroupPage(page: number) {
    setGroupPage(page);
    setGroupKey(null);
    setNodePage(0);
    setQuery('');
  }

  if (!root) return <EmptyState compact title="Kein Ausgangspunkt gewählt" />;

  return (
    <section className="orbit-view" aria-label={`Beziehungsorbit für ${root.label}`}>
      <div className="orbit-layout">
        <div className="orbit-map-panel">
          <div className="orbit-map-heading">{neighbourCount} direkt verbunden</div>
          <div className="orbit-map">
            <svg className="orbit-lines" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
              <ellipse className="orbit-track orbit-track-outer" cx="50" cy="50" rx="43" ry="44" />
              <ellipse className="orbit-track" cx="50" cy="50" rx="34" ry="35" />
              <ellipse className="orbit-track orbit-track-inner" cx="50" cy="50" rx="22" ry="23" />
              {visibleGroups.map((group, index) => {
                const { x, y } = satellitePosition(index, visibleGroups.length);
                return <line key={group.key} className={group.key === activeGroup?.key ? 'orbit-connection is-active' : 'orbit-connection'} x1="50" y1="50" x2={x} y2={y} />;
              })}
            </svg>

            <button
              type="button"
              className={`orbit-center${selectedId === rootId ? ' is-selected' : ''}`}
              style={entityStyle(root.klass)}
              onClick={() => onSelect(rootId)}
              aria-label={`${root.label}, Mittelpunkt. Details anzeigen`}
              aria-pressed={selectedId === rootId}
              title={root.label}
            >
              <span className="orbit-center-icon"><EntityIcon klass={root.klass} /></span>
              <strong>{root.label}</strong>
              <span className="orbit-center-class">{classLabel(root.klass)}</span>
            </button>

            <div className="orbit-satellites" role="group" aria-label="Beziehungsgruppen">
              {visibleGroups.map((group, index) => {
                const { x, y } = satellitePosition(index, visibleGroups.length);
                return (
                  <button
                    key={group.key}
                    type="button"
                    className={`orbit-satellite${activeGroup?.key === group.key ? ' is-active' : ''}`}
                    style={{ ...entityStyle(group.klass), left: `${x}%`, top: `${y}%` }}
                    onClick={() => chooseGroup(group.key)}
                    aria-pressed={activeGroup?.key === group.key}
                    aria-controls={resultsId}
                    aria-label={`${group.label}: ${group.nodes.length} Einträge, ${group.direction === 'out' ? 'ausgehend' : 'eingehend'}. Anzeigen`}
                    title={group.label}
                  >
                    <span className="orbit-satellite-top">
                      <span className="orbit-entity-icon"><EntityIcon klass={group.klass} /></span>
                      <strong className="orbit-satellite-count">{group.nodes.length}</strong>
                      <Icon name={group.direction === 'out' ? 'arrow-right' : 'arrow-left'} className="orbit-satellite-direction" />
                    </span>
                    <span className="orbit-satellite-label">{group.label}</span>
                    <span className="orbit-satellite-class">{classLabel(group.klass)}</span>
                  </button>
                );
              })}
            </div>
          </div>
          {groupPages > 1 && (
          <div className="orbit-map-footer">
              <nav className="orbit-pagination" aria-label="Beziehungsgruppen blättern">
                <button type="button" onClick={() => changeGroupPage(currentGroupPage - 1)} disabled={currentGroupPage === 0} aria-label="Vorherige Beziehungsgruppen"><Icon name="arrow-left" /></button>
                <span>{currentGroupPage + 1} / {groupPages}</span>
                <button type="button" onClick={() => changeGroupPage(currentGroupPage + 1)} disabled={currentGroupPage === groupPages - 1} aria-label="Weitere Beziehungsgruppen"><Icon name="arrow-right" /></button>
              </nav>
          </div>
          )}
        </div>

        <div className="orbit-browser" id={resultsId}>
          {activeGroup ? (
            <>
              <div className="orbit-browser-heading" style={entityStyle(activeGroup.klass)}>
                <span className="orbit-entity-icon"><EntityIcon klass={activeGroup.klass} /></span>
                <h3>{activeGroup.label}</h3>
                <Chip>{activeGroup.nodes.length}</Chip>
              </div>
              <label className="orbit-search" htmlFor={searchId}>
                <Icon name="search" />
                <input id={searchId} type="search" value={query} onChange={(event) => { setQuery(event.target.value); setNodePage(0); }} placeholder="In dieser Gruppe suchen …" aria-label="Verbundene Einträge durchsuchen" />
              </label>
              <div className="orbit-results-summary" aria-live="polite" aria-atomic="true">
                {filteredNodes.length === 0 ? 'Keine passenden Einträge' : `${currentNodePage * NODE_PAGE_SIZE + 1}–${Math.min((currentNodePage + 1) * NODE_PAGE_SIZE, filteredNodes.length)} von ${filteredNodes.length} Einträgen`}
              </div>
              <ul className="orbit-node-list">
                {visibleNodes.map((node) => (
                  <li key={node.id} className={`orbit-node${selectedId === node.id ? ' is-selected' : ''}`} style={entityStyle(node.klass)}>
                    <button type="button" className="orbit-node-select" onClick={() => onSelect(node.id)} aria-pressed={selectedId === node.id} title={node.label}>
                      <span className="orbit-entity-icon"><EntityIcon klass={node.klass} /></span>
                      <span className="orbit-node-text"><strong>{node.label}</strong><span>{node.detail || classLabel(node.klass)}</span></span>
                    </button>
                    <button type="button" className="orbit-focus-button" onClick={() => onRootChange(node.id)} aria-label={`${node.label} in den Mittelpunkt setzen`} title="In den Mittelpunkt setzen">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true"><circle cx="12" cy="12" r="5" /><path d="M12 2v4m0 12v4M2 12h4m12 0h4" strokeLinecap="round" /></svg>
                      <span>Als Mittelpunkt</span>
                    </button>
                  </li>
                ))}
              </ul>
              {filteredNodes.length === 0 && <EmptyState compact>Für „{query}“ gibt es in dieser Gruppe keine Treffer.</EmptyState>}
              {nodePages > 1 && (
                <nav className="orbit-pagination orbit-node-pagination" aria-label="Verbundene Einträge blättern">
                  <button type="button" onClick={() => setNodePage(currentNodePage - 1)} disabled={currentNodePage === 0} aria-label="Vorherige Einträge"><Icon name="arrow-left" /></button>
                  <span>Seite {currentNodePage + 1} von {nodePages}</span>
                  <button type="button" onClick={() => setNodePage(currentNodePage + 1)} disabled={currentNodePage === nodePages - 1} aria-label="Weitere Einträge"><Icon name="arrow-right" /></button>
                </nav>
              )}
            </>
          ) : (
            <EmptyState title="Keine Beziehungen">
              Für diesen Eintrag sind keine direkten Beziehungen vorhanden.
            </EmptyState>
          )}
        </div>
      </div>
    </section>
  );
}
